
import React from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Printer, CheckCircle, ListChecks, AlertCircle, Speech, Cloud } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

import { ConversationEntry } from '../../types';

interface AnalysisResultsProps {
  analysisResults: string;
  conversation: ConversationEntry[];
  isSaved?: boolean;
}

export const AnalysisResults: React.FC<AnalysisResultsProps> = ({ 
  analysisResults,
  conversation,
  isSaved = false
}) => {
  const renderMarkdown = (markdown: string) => {
    if (!markdown) return '';
    let parsed = markdown
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-5 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-6 mb-4">$1</h1>');
    parsed = parsed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    parsed = parsed.replace(/\*(.*?)\*/g, '<em>$1</em>');
    parsed = parsed.replace(/__(.*?)__/g, '<strong>$1</strong>');
    parsed = parsed.replace(/_(.*?)_/g, '<em>$1</em>');
    parsed = parsed.replace(/`(.*?)`/g, '<code class="bg-muted text-primary px-1 rounded">$1</code>');
    parsed = parsed.replace(/\n/g, '<br/>');
    return parsed;
  };

  const handleCopySummary = () => {
    if (analysisResults) {
      // Copy the original markdown text, not the HTML
      navigator.clipboard.writeText(analysisResults)
        .then(() => {
          toast({
            title: "Summary Copied",
            description: "The conversation summary has been copied to your clipboard.",
          });
        })
        .catch(err => {
          console.error("Failed to copy summary:", err);
          toast({
            title: "Copy Failed",
            description: "Failed to copy the summary. Please try again.",
            variant: "destructive",
          });
        });
    }
  };

  const handlePrintConversation = () => {
    if (!conversation.length || !analysisResults) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({
        title: "Print Failed",
        description: "Unable to open print window. Please check your popup settings.",
        variant: "destructive",
      });
      return;
    }

    const conversationHtml = conversation.map((entry, index) => `
      <div style="margin-bottom: 20px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px;">
        <h3 style="margin-top: 0; color: #4338ca; font-weight: bold;">${entry.agent} (${entry.persona})</h3>
        <p style="margin-bottom: 0;">${entry.message.replace(/\n/g, '<br/>')}</p>
        <div style="font-size: 0.75rem; color: #64748b; margin-top: 8px;">Model: ${entry.model.split('/').pop()}</div>
      </div>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>AI Agents Conversation Analysis</title>
        <meta charset="utf-8">
        <style>
          body { font-family: system-ui, -apple-system, sans-serif; line-height: 1.5; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1e293b; margin-bottom: 30px; }
          h2 { color: #334155; margin-top: 30px; margin-bottom: 15px; }
          hr { border: 0; height: 1px; background: #e2e8f0; margin: 30px 0; }
          .analysis { white-space: pre-wrap; }
          pre { background-color: #f8fafc; padding: 15px; border-radius: 8px; overflow-x: auto; }
          code { font-family: monospace; }
          ul, ol { margin-left: 20px; }
          blockquote { border-left: 4px solid #e2e8f0; padding-left: 15px; margin-left: 0; font-style: italic; }
        </style>
      </head>
      <body>
        <h1>AI Agents Conversation Analysis</h1>
        
        <h2>Conversation Analysis</h2>
        <div class="analysis">${renderMarkdown(analysisResults)}</div>
        
        <hr>
        
        <h2>Full Conversation</h2>
        ${conversationHtml}
        
        <footer style="margin-top: 50px; font-size: 0.8rem; color: #94a3b8; text-align: center;">
          Generated by AI Agents Lab on ${new Date().toLocaleDateString()}
        </footer>
      </body>
      </html>
    `);

    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Compute simple contributions and token estimates
  const agentCounts = conversation.reduce((acc: Record<string, number>, entry) => {
    acc[entry.agent] = (acc[entry.agent] || 0) + 1;
    return acc;
  }, {});
  const chartData = Object.entries(agentCounts).map(([agent, count]) => ({ agent, count }));
  const totalChars = conversation.reduce((sum, e) => sum + e.message.length, 0) + (analysisResults?.length || 0);
  const estTokens = Math.max(1, Math.round(totalChars / 4)); // ~4 chars/token heuristic
  const uniqueModels = Array.from(new Set(conversation.map(e => e.model))).length;
  const allFree = conversation.length > 0 && conversation.every(e => e.model.includes(':free'));
  const estCostDisplay = allFree ? '$0.00' : '—'; // Pricing varies by model/provider

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">Analysis Results</h3>
            {isSaved && (
              <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                <Cloud className="h-3 w-3" />
                Saved
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCopySummary} className="h-8">
              <Copy className="h-3.5 w-3.5 mr-1" />
              Copy
            </Button>
            <Button 
              onClick={handlePrintConversation} 
              variant="outline"
              size="sm"
              className="h-8"
            >
              <Printer className="h-3.5 w-3.5 mr-1" />
              Print
            </Button>
          </div>
        </div>
        {/* Run Stats */}
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4 mb-4">
            <div className="rounded-md border bg-card text-card-foreground p-3">
              <div className="text-xs text-muted-foreground">Estimated tokens</div>
              <div className="text-base font-medium">{estTokens.toLocaleString()}</div>
            </div>
            <div className="rounded-md border bg-card text-card-foreground p-3">
              <div className="text-xs text-muted-foreground">Messages</div>
              <div className="text-base font-medium">{conversation.length}</div>
            </div>
            <div className="rounded-md border bg-card text-card-foreground p-3">
              <div className="text-xs text-muted-foreground">Models used</div>
              <div className="text-base font-medium">{uniqueModels}</div>
            </div>
            <div className="rounded-md border bg-card text-card-foreground p-3">
              <div className="text-xs text-muted-foreground">Est. cost</div>
              <div className="text-base font-medium">{estCostDisplay}</div>
            </div>
          </div>

        {/* Agent Contributions Chart */}
        {chartData.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Agent Contributions</h4>
            <ChartContainer
              config={{
                count: { label: 'Messages', color: 'hsl(var(--primary))' }
              }}
              className="w-full h-56"
            >
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="agent" />
                <YAxis allowDecimals={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </div>
        )}

        {/* Analysis markdown */}
        <div 
          className="text-foreground bg-muted p-5 rounded-md border prose prose-sm max-w-none max-h-[500px] overflow-y-auto select-text cursor-text"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(analysisResults) }}
        />
        <div className="flex flex-wrap gap-3 mt-4">
          <div className="flex items-center text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
            <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
            <span>Key Insights</span>
          </div>
          <div className="flex items-center text-sm bg-accent text-accent-foreground px-3 py-1 rounded-full">
            <ListChecks className="h-3.5 w-3.5 mr-1.5" />
            <span>Discussion Points</span>
          </div>
          <div className="flex items-center text-sm bg-destructive/10 text-destructive px-3 py-1 rounded-full">
            <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
            <span>Areas of Disagreement</span>
          </div>
          <div className="flex items-center text-sm bg-secondary/10 text-secondary-foreground px-3 py-1 rounded-full">
            <Speech className="h-3.5 w-3.5 mr-1.5" />
            <span>Further Exploration</span>
          </div>
        </div>
      </div>
    </div>
  );
};
